
import { 
  Shield,
  FileText, 
  ClipboardCheck, 
  BookOpen,
  GraduationCap,
  Users
} from "lucide-react";

interface ServiceCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

const ServiceCard = ({ title, description, icon }: ServiceCardProps) => {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-md hover:border-purple-100 h-full flex flex-col">
      <div className="p-3 rounded-lg bg-purple-50 w-fit mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-3 text-gray-900">{title}</h3>
      <p className="text-gray-600 flex-grow">{description}</p>
    </div>
  );
};

export const ServiceCards = () => {
  const services = [
    {
      title: "Policy Development",
      description: "Custom cybersecurity policies aligned with industry standards and regulatory requirements for your organization.",
      icon: <FileText className="w-6 h-6 text-edgefi-purple" />
    },
    {
      title: "Procedures Documentation",
      description: "Step-by-step procedures that detail precisely how policies should be implemented by staff.",
      icon: <ClipboardCheck className="w-6 h-6 text-edgefi-purple" />
    },
    {
      title: "Compliance Mapping",
      description: "Mapping policies to frameworks like NIST CSF, ISO 27001, HIPAA, and other regulatory requirements.",
      icon: <Shield className="w-6 h-6 text-edgefi-purple" />
    },
    {
      title: "Policy Library",
      description: "Comprehensive document library with templates that can be customized to your organization's needs.",
      icon: <BookOpen className="w-6 h-6 text-edgefi-purple" />
    },
    {
      title: "Staff Training",
      description: "Training sessions to ensure your team understands and can effectively implement new policies.",
      icon: <GraduationCap className="w-6 h-6 text-edgefi-purple" />
    },
    {
      title: "Stakeholder Alignment",
      description: "Working with key stakeholders to ensure policies are practical, effective, and aligned with business goals.",
      icon: <Users className="w-6 h-6 text-edgefi-purple" />
    }
  ];

  return (
    <section className="py-16 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Comprehensive Policy Services</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Our policy development services help organizations establish clear guidelines, increase security awareness, and meet compliance requirements.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard 
              key={index}
              title={service.title}
              description={service.description}
              icon={service.icon}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
